import _, {difference} from 'lodash';
import {Middleware} from 'redux';

import {logger} from '../../srcCommon/services/logger/logger';
import {isDraftMessage} from '../helpers/models/messageHelpers';
import {DraftMessageModel} from '../models/api/messageModel';
import {AppAction} from '../store/appActions';
import {AppState} from '../store/appState';
import {getMessages} from '../store/data/dataSelectors';
import {MessageActionsEnum, MessageDeletedAction} from '../store/data/messages/messagesActions';
import {getComposerPanelsConversationIds} from '../store/panels/composer/composerPanelSelectors';
import {getViewerPanelsConversationIds} from '../store/panels/viewer/viewerPanelSelectors';

const handleTimeChangesLogger = logger.getLogger('handleTimeChanges');
const handleTimeLogger = logger.getLogger('handleTime');

const MAX_HANDLE_TIME_INCREMENT = 60 * 60; // 1h

/*
 * Active conversation timers.
 */

export class ConversationTimer {
  lastSaveAt = performance.now();
  conversationOpenedAt = performance.now();

  reserveCurrentIncrement() {
    // The amount to add to the draft, in seconds.
    const now = performance.now();
    const currentIncrement = Math.round((now - this.lastSaveAt) / 1000);

    // Remove immediately so it doesn't get counted twice. Will re-add if save fails.
    this.lastSaveAt = now;
    return currentIncrement;
  }

  cancelUnsavedIncrement(unsavedIncrement: number) {
    // This increment was not saved as handle time, let's add it back for the next save attempt.
    this.lastSaveAt -= unsavedIncrement * 1000;
  }

  cancelCurrentDraft() {
    // Discard the time accumulated on the draft and reset to when conversation was opened.
    this.lastSaveAt = this.conversationOpenedAt;
  }

  reset() {
    this.lastSaveAt = performance.now();
    this.conversationOpenedAt = performance.now();
  }
}

/*
 * Setup.
 */

const conversationTimers: {[conversationId: number]: ConversationTimer} = {};
let previousTimedConversationIds: number[];

export function getConversationTimer(conversationId: number) {
  if (!conversationTimers[conversationId]) {
    return null;
  }

  return conversationTimers[conversationId];
}

/*
 * Redux middleware.
 */

export const handleTimeMiddleware: Middleware =
  ({getState}) =>
  (next) => {
    function afterAction(action: AppAction) {
      if (action.type === MessageActionsEnum.MESSAGE_DELETED) {
        resetDeletedDraftTimer(action, getState());
        return;
      }

      // Handle viewed conversations changes by comparing current and previous versions of the state, to avoid duplicating the viewer logic.
      const viewedConversationIds: number[] = getViewerPanelsConversationIds(getState());
      const newComposeConversationIds: number[] = getComposerPanelsConversationIds(getState());

      const timedConversationIds = _.uniq([...viewedConversationIds, ...newComposeConversationIds]);

      if (_.isEqual(timedConversationIds, previousTimedConversationIds)) {
        return;
      }

      // Create the new active handle time.
      const addedConversationIds = difference(timedConversationIds, previousTimedConversationIds);
      addedConversationIds.forEach((id) => {
        conversationTimers[id] = new ConversationTimer();
      });

      // Delete the active handle time of conversation the user doesn't see anymore.
      const removedConversationIds = difference(previousTimedConversationIds, timedConversationIds);
      removedConversationIds.forEach((id) => {
        delete conversationTimers[id];
      });

      previousTimedConversationIds = timedConversationIds;
    }

    return (action: AppAction) => {
      next(action);

      try {
        afterAction(action);
      } catch (err) {
        handleTimeChangesLogger.error('Error in handle time middleware', err);
      }
    };
  };

/*
 * Side effects.
 */

export function reserveHandleTimeToSave(message: DraftMessageModel) {
  // Reserve the current handle time increment for a draft save query.
  const timer = getConversationTimer(message.conversationId);
  const rawIncrement = timer ? timer.reserveCurrentIncrement() : 0;
  const shouldAddIncrement = rawIncrement < MAX_HANDLE_TIME_INCREMENT;
  const handleTimeIncrement = shouldAddIncrement ? rawIncrement : 0;

  if (rawIncrement > 3600) {
    handleTimeLogger.info('handleTimeIncrement when computing', {
      handleTimeIncrement,
      rawIncrement,
      lastSaveAt: timer?.lastSaveAt,
      conversationOpenedAt: timer?.conversationOpenedAt,
    });
  }

  return {
    handleTimeIncrement,
    cancelUnsavedHandleTime: () => cancelUnsavedHandleTime(message.conversationId, handleTimeIncrement),
  };
}

export function resetConversationTimer(conversationId: number) {
  // When a draft is sent, the handle time is saved and will count for that conversation, we can restart counting time from 0.
  const timer = getConversationTimer(conversationId);
  if (timer) {
    timer.reset();
  }
}

function cancelUnsavedHandleTime(conversationId: number, unsavedIncrement: number) {
  const timer = getConversationTimer(conversationId);
  if (timer) {
    timer.cancelUnsavedIncrement(unsavedIncrement);
  }
}

function resetDeletedDraftTimer(action: MessageDeletedAction, state: AppState) {
  const {messageUid} = action.payload;
  const message = getMessages(state)[messageUid];
  if (!message || !isDraftMessage(message)) {
    return;
  }

  const timer = getConversationTimer(message.conversationId);
  if (timer) {
    timer.cancelCurrentDraft();
  }
}
